// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, Spinner, Textarea, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { ArrowUp16Filled, AttachRegular, DismissRegular, MicFilled, WandRegular } from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useRef, useState } from 'react';
import { Constants } from '../../Constants';
import { COPY } from '../../assets/strings';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { useMediaQuery } from '../../libs/hooks/useMediaQuery';
import { GetResponseOptions } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import { editConversationInput, updateBotResponseStatus } from '../../redux/features/conversations/conversationsSlice';
import { Alerts } from '../shared/Alerts';
import { getErrorDetails } from '../utils/TextUtils';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { customTokens, Breakpoints, breakpointUp } from '../../styles';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    root: {
        width: '100%',
        padding: '0 24px',
        ...Breakpoints.small({
            padding: '0 16px',
        }),
    },
    contentWrapper: {
        padding: '12px',
        backgroundColor: customTokens.colorContentWrapper,
        borderRadius: '8px',
        boxShadow: tokens.shadow8,
        margin: '0 auto',
        ...Breakpoints.small({
            padding: '8px',
            borderRadius: '12px',
        }),
    },
    content: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        ...Breakpoints.small({
            ...shorthands.gap(tokens.spacingHorizontalS),
        }),
    },
    inputContainer: {
        position: 'relative',
        width: '100%',
    },
    input: {
        width: '100%',
        backgroundColor: customTokens.colorInputBackground,
    },
    textarea: {
        minHeight: '114px',
        position: 'relative',
        padding: '16px',
        border: '0',
        ...Breakpoints.small({
            minHeight: '40px',
            maxHeight: '40px',
            padding: '12px',
            overflowY: 'auto',
            resize: 'none',
            lineHeight: '1.2',
        }),
    },
    placeholderContainer: {
        position: 'absolute',
        top: '16px',
        left: '16px',
        right: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        pointerEvents: 'none',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        zIndex: 1,
        ...Breakpoints.small({
            top: '12px',
            left: '12px',
            right: '12px',
            gap: '6px',
            fontSize: tokens.fontSizeBase200,
        }),
    },
    placeholderIcon: {
        fontSize: '20px',
        flexShrink: '0',
        ...Breakpoints.small({
            fontSize: '16px',
        }),
    },
    placeholderText: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        flex: '1',
        minWidth: '0',
        ...Breakpoints.small({
            fontSize: '12px',
        }),
    },
    controls: {
        display: 'flex',
        marginTop: '12px',
        ...Breakpoints.small({
            marginTop: '8px',
        }),
    },
    essentials: {
        display: 'flex',
        marginLeft: 'auto',
        gap: '8px',
        ...Breakpoints.small({
            gap: '6px',
        }),
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },
    mic: {
        width: '32px',
        height: '32px',
        backgroundColor: customTokens.colorMeBackground,
        borderRadius: tokens.borderRadiusLarge,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    send: {
    },
    sendButton: {
        borderRadius: tokens.borderRadiusLarge,
        color: customTokens.colorSendButton,
        backgroundColor: customTokens.colorBlackButton,
    },
    warning: {
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '20px',
        color: customTokens.colorTextDisabled,
        padding: '12px 0',
        ...Breakpoints.small({
            fontSize: '10px',
            lineHeight: '16px',
            padding: '8px 0',
        }),
    },
    documentButton: {
        borderRadius: tokens.borderRadiusLarge,
        ...Breakpoints.small({
            minWidth: '32px',
        }),
    },
    documentFileInput: {
        display: 'none',
    },
    uploadDialog: {
        maxWidth: '400px',
    },
    uploadDialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        padding: '20px',
    },
    uploadDialogText: {
        textAlign: 'center',
        fontSize: tokens.fontSizeBase300,
    },
    fileIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '5px 10px',
        backgroundColor: customTokens.colorMeBackground,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase200,
        marginLeft: '8px',
        color: tokens.colorNeutralForegroundStaticInverted,
        fontWeight: '500',
        boxShadow: tokens.shadow4,
        animation: 'popGrow 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
    },
    fileIndicatorName: {
        maxWidth: '150px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fileRemoveButton: {
        minWidth: '20px',
        width: '20px',
        height: '20px',
        padding: '0',
        color: tokens.colorNeutralForegroundStaticInverted,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.2)',
            color: tokens.colorNeutralForegroundStaticInverted,
        },
    },
    fileIndicatorRemoving: {
        animation: 'burst 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
    },
});

interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<void>;
    selectedAgentId: string;
    isSuperAgent: boolean;
    isDocumentUploadEnabled: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
    isDraggingOver,
    onDragLeave,
    onSubmit,
    selectedAgentId,
    isSuperAgent: _isSuperAgent,
    isDocumentUploadEnabled,
}) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();
    const dispatch = useAppDispatch();
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);

    const isSmUp = useMediaQuery(breakpointUp('sm'));

    const [value, setValue] = useState('');
    const [recognizer, setRecognizer] = useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = useState(false);
    const [isUploadDialogOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [isFileRemoving, setIsFileRemoving] = useState(false);
    const { importingDocuments } = conversations[selectedId];

    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
    React.useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
    }, [selectedId]);

    // Clear pending file when conversation changes
    React.useEffect(() => {
        setPendingFile(null);
    }, [selectedId]);

    React.useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService();
            const response = await speechService.getSpeechTokenAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            if (response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }

        initSpeechRecognizer().catch((e) => {
            const errorDetails = getErrorDetails(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.disabled ? COPY.CHAT_DELETED_MESSAGE() : chatState.input);
    }, [conversations, selectedId]);

    const handleSpeech = () => {
        setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleFileChange = () => {
        const files = documentFileRef.current?.files;
        if (files && files.length > 0) {
            const allowedExtensions = Constants.app.importTypes.split(',').map(ext => ext.toLowerCase().trim());
            const invalidFiles: string[] = [];

            for (const file of Array.from(files)) {
                const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
                if (!allowedExtensions.includes(fileExtension)) {
                    invalidFiles.push(file.name);
                }
            }

            if (invalidFiles.length > 0) {
                dispatch(addAlert({
                    message: `Desteklenmeyen dosya formatı: ${invalidFiles.join(', ')}. İzin verilen formatlar: ${Constants.app.importTypes}`,
                    type: AlertType.Error
                }));
                if (documentFileRef.current) {
                    documentFileRef.current.value = '';
                }
                return;
            }

            // Store the file in state instead of immediately uploading
            // It will be sent together with the message when the user submits
            setPendingFile(files[0]);

            if (documentFileRef.current) {
                documentFileRef.current.value = '';
            }
        }
    };

    const handleRemovePendingFile = () => {
        setIsFileRemoving(true);
        setTimeout(() => {
            setPendingFile(null);
            setIsFileRemoving(false);
            if (documentFileRef.current) {
                documentFileRef.current.value = '';
            }
        }, 500);
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '' && !pendingFile) {
            return; // only submit if value is not empty or there's a pending file
        }

        const currentFile = pendingFile;
        setValue('');
        setPendingFile(null);
        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'Cevap bekleniyor' }));
        onSubmit({
            value: value.trim() || (currentFile ? `Dosya yüklendi: ${currentFile.name}` : ''),
            messageType,
            chatId: selectedId,
            agentId: selectedAgentId,
            file: currentFile ?? undefined,
        })
            .catch((error) => {
                const message = `Error submitting chat input: ${(error as Error).message}`;
                log(message);
                dispatch(
                    addAlert({
                        type: AlertType.Error,
                        message,
                    }),
                );
            });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);

        const files = e.dataTransfer.files;
        const allowedExtensions = Constants.app.importTypes.split(',').map(ext => ext.toLowerCase().trim());
        const invalidFiles: string[] = [];

        for (const file of Array.from(files)) {
            const fileExtension = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
            if (!allowedExtensions.includes(fileExtension)) {
                invalidFiles.push(file.name);
            }
        }

        if (invalidFiles.length > 0) {
            dispatch(addAlert({
                message: `Desteklenmeyen dosya formatı: ${invalidFiles.join(', ')}. İzin verilen formatlar: ${Constants.app.importTypes}`,
                type: AlertType.Error
            }));
            return;
        }

        // Store the dropped file in state instead of immediately uploading
        if (files.length > 0) {
            setPendingFile(files[0]);
        }
    };

    return (
        <div className={classes.root}>
            <style>{`
                @keyframes popGrow {
                    0% { opacity: 0; transform: scale(0.4); }
                    60% { opacity: 1; transform: scale(1.2); }
                    100% { opacity: 1; transform: scale(1); }
                }
                @keyframes burst {
                    0% { opacity: 1; transform: scale(1) rotateZ(0deg); }
                    50% { opacity: 1; transform: scale(1.3) rotateZ(8deg); }
                    100% { opacity: 0; transform: scale(0) rotateZ(20deg); }
                }
            `}</style>
            <Alerts />
            <div className={classes.contentWrapper}>
                <div className={classes.content}>
                    <div className={classes.inputContainer}>
                        <Textarea
                            title="Sohbet girişi"
                            aria-label="Sohbet giriş alanı. Enter tuşuna basınız."
                            ref={textAreaRef}
                            id="chat-input"
                            resize="vertical"
                            disabled={conversations[selectedId].disabled || isUploadDialogOpen}
                            textarea={{
                                className: isDraggingOver
                                    ? mergeClasses(classes.dragAndDrop, classes.textarea)
                                    : classes.textarea,
                            }}
                            className={classes.input}
                            value={isDraggingOver ? 'Dosyalarınızı buraya bırakınız' : value}
                            onDrop={(e) => { handleDrop(e); }}
                            onFocus={() => {
                                // update the locally stored value to the current value
                                const chatInput = document.getElementById('chat-input');
                                if (chatInput) {
                                    setValue((chatInput as HTMLTextAreaElement).value);
                                }
                                // User is considered typing if the input is in focus
                                if (activeUserInfo) {
                                    dispatch(
                                        updateUserIsTyping({
                                            userId: activeUserInfo.id,
                                            chatId: selectedId,
                                            isTyping: true,
                                        }),
                                    );
                                }
                            }}
                            onChange={(_event, data) => {
                                if (isDraggingOver) {
                                    return;
                                }

                                setValue(data.value);
                                dispatch(editConversationInput({ id: selectedId, newInput: data.value }));
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                    event.preventDefault();
                                    handleSubmit(value);
                                }
                            }}
                            onBlur={() => {
                                // User is considered not typing if the input is not  in focus
                                if (activeUserInfo) {
                                    dispatch(
                                        updateUserIsTyping({
                                            userId: activeUserInfo.id,
                                            chatId: selectedId,
                                            isTyping: false,
                                        }),
                                    );
                                }
                            }}
                        />
                        {!value && !isDraggingOver && !pendingFile && (
                            <div className={classes.placeholderContainer}>
                                <WandRegular className={classes.placeholderIcon} />
                                <span className={classes.placeholderText}>
                                    Yapay zekaya bir soru sorun veya bir istekte bulunun...
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={classes.controls}>
                    <div className={classes.functional}>
                        {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                        <input
                            type="file"
                            ref={documentFileRef}
                            className={classes.documentFileInput}
                            accept={Constants.app.importTypes}
                            multiple={false}
                            onChange={() => { handleFileChange(); }}
                        />
                        {isDocumentUploadEnabled && (
                            <Button
                                disabled={
                                    conversations[selectedId].disabled ||
                                    ((importingDocuments?.length ?? 0) > 0) ||
                                    isUploadDialogOpen ||
                                    pendingFile !== null
                                }
                                size="small"
                                icon={<AttachRegular />}
                                onClick={() => documentFileRef.current?.click()}
                                title="Dosya ekle"
                                aria-label="Dosya ekleme düğmesi"
                                iconPosition="after"
                                className={classes.documentButton}
                            >
                                {isSmUp ? 'Dosya Ekle' : ''}
                            </Button>
                        )}
                        {pendingFile && (
                            <div className={mergeClasses(classes.fileIndicator, isFileRemoving && classes.fileIndicatorRemoving)}>
                                <AttachRegular fontSize={14} />
                                <span className={classes.fileIndicatorName} title={pendingFile.name}>
                                    {pendingFile.name}
                                </span>
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<DismissRegular fontSize={12} />}
                                    className={classes.fileRemoveButton}
                                    onClick={handleRemovePendingFile}
                                    title="Dosyayı kaldır"
                                />
                            </div>
                        )}
                        {importingDocuments && importingDocuments.length > 0 && <Spinner size="tiny" />}
                    </div>
                    <div className={classes.essentials}>
                        {recognizer && (
                            <Button
                                appearance="primary"
                                disabled={conversations[selectedId].disabled || isListening || isUploadDialogOpen}
                                icon={<MicFilled />}
                                onClick={handleSpeech}
                            />
                        )}
                        <div className={classes.send}>
                            <Button
                                title="Gönder"
                                aria-label="Gönderme düğmesi"
                                appearance="primary"
                                className={classes.sendButton}
                                icon={<ArrowUp16Filled />}
                                onClick={() => {
                                    handleSubmit(value);
                                }}
                                disabled={conversations[selectedId].disabled || isUploadDialogOpen}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.warning}>
                Cevaplarım yapay zekaya dayalıdır, lütfen karar almadan önce doğruluğunu kontrol edin.
            </div>
            <Dialog open={isUploadDialogOpen} modalType="modal">
                <DialogSurface className={classes.uploadDialog}>
                    <DialogBody>
                        <DialogTitle>Dosya Yükleniyor</DialogTitle>
                        <DialogContent className={classes.uploadDialogContent}>
                            <Spinner size="large" />
                            <div className={classes.uploadDialogText}>
                                Dosyanız yükleniyor, lütfen bekleyiniz...
                            </div>
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};
